// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/PaymentBillingCard.jsx
// *******************************************************************************
// GOODS RECEIPT PAYMENT & BILLING CARD (FATURA E PAGAMENTOS)
// *******************************************************************************
// Two sections:
// 1. Billing info (payment.billing) - invoice number, amounts
// 2. Installments table (managed as state, like items) - add/edit/remove
// Payment methods (payment.methods) are displayed in the installment table
// via the `type` field on each installment (tPag code from SEFAZ).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Tag,
  Popconfirm,
  Empty,
  Table,
  Space,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';
import InstallmentModal from '../../modals/InstallmentModal';
import dayjs from 'dayjs';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PaymentBillingCard = ({
  installments = [],
  onAddInstallment,
  onUpdateInstallment,
  onRemoveInstallment,
  viewMode,
  data,
  onActionsChange,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { submitForm } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isConfirmed = data?.status === 'confirmed';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Expose add handler to parent for Collapse extra button
  useEffect(() => {
    onActionsChange?.({
      handleAddClick: () => {
        setEditingItem(null);
        setEditingIndex(null);
        setShowModal(true);
      },
    });
  }, [onActionsChange]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEditClick = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleModalConfirm = useCallback(
    installmentData => {
      if (editingIndex !== null) {
        onUpdateInstallment(editingIndex, installmentData);
      } else {
        onAddInstallment(installmentData);
      }
      setShowModal(false);
      setEditingItem(null);
      setEditingIndex(null);

      // Auto-save after installment change
      setTimeout(() => submitForm?.(), 100);
    },
    [editingIndex, onAddInstallment, onUpdateInstallment, submitForm],
  );

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // Build payment method lookup from payment.methods (imported from XML)
  const paymentMethods = data?.payment?.methods || [];

  // Installments table columns
  const installmentColumns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'number',
        width: 60,
        align: 'center',
      },
      {
        title: translateX('due_date'),
        dataIndex: 'dueDate',
        width: 120,
        align: 'center',
        render: val => {
          if (!val) return '—';
          const d = dayjs(val);
          return d.isValid() ? d.format('DD/MM/YYYY') : '—';
        },
      },
      {
        title: translateX('payment_method'),
        dataIndex: 'type',
        width: 180,
        render: (val, _record, index) => {
          // First try the installment's own type field (set manually)
          const code = val || paymentMethods[index]?.type;
          if (!code) return '—';
          const paymentType = fiscalService.getPaymentTypeByCode(code);
          return (
            <Space size={4}>
              <Tag>{code}</Tag>
              <span>{translateX(paymentType?.labelKey || code)}</span>
            </Space>
          );
        },
      },
      {
        title: translateX('amount'),
        dataIndex: 'amount',
        width: 120,
        align: 'right',
        render: val => (
          <Text strong>
            {financialService.formatCurrency(val || 0)}
          </Text>
        ),
      },
      {
        title: translateX('actions'),
        width: 90,
        align: 'center',
        render: (_, record, index) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record, index)}
              disabled={isLocked || isConfirmed}
            />
            <Popconfirm
              title={translateX('remove_installment_confirm')}
              onConfirm={() => {
                onRemoveInstallment(index);
                setTimeout(() => submitForm?.(), 100);
              }}
              okText={translateX('yes')}
              cancelText={translateX('no')}
              disabled={isLocked || isConfirmed}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isLocked || isConfirmed}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [isLocked, isConfirmed, onRemoveInstallment, submitForm, paymentMethods],
  );

  // Installments total
  const installmentsTotal = useMemo(() => {
    return installments.reduce((sum, inst) => sum + (inst.amount || 0), 0);
  }, [installments]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Section 1: Billing Info */}
      <Text strong className="text-muted">
        {translateX('billing_info')}
      </Text>
      <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={6}>
          <SimpleInput
            name={['payment', 'billing', 'number']}
            label="billing_number"
            disabled={isLocked || isConfirmed}
          />
        </Col>
        <Col xs={24} md={6}>
          <SimpleCurrency
            name={['payment', 'billing', 'originalAmount']}
            label="billing_original_amount"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
        <Col xs={24} md={6}>
          <SimpleCurrency
            name={['payment', 'billing', 'discountAmount']}
            label="billing_discount_amount"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
        <Col xs={24} md={6}>
          <SimpleCurrency
            name={['payment', 'billing', 'netAmount']}
            label="billing_net_amount"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
      </Row>

      <Divider style={{ margin: '8px 0 12px' }} />

      {/* Section 2: Installments Table */}
      <Text strong className="text-muted">
        {translateX('installments')}
      </Text>
      <div style={{ marginTop: 8 }}>
        {installments.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translateX('no_installments_added')}
          />
        ) : (
          <SimpleTable
            columns={installmentColumns}
            dataSource={installments}
            rowKey={getInstallmentRowKey}
            pagination={false}
            size="small"
            scroll={{ x: 500 }}
            onRow={(record, index) => ({
              onDoubleClick: () =>
                !isLocked && !isConfirmed && handleEditClick(record, index),
            })}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3} align="right">
                  <Text strong>{translateX('total_installments')}:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>
                    {financialService.formatCurrency(installmentsTotal)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
              </Table.Summary.Row>
            )}
          />
        )}
      </div>

      {/* Installment Modal */}
      <InstallmentModal
        show={showModal}
        setShow={setShowModal}
        onConfirm={handleModalConfirm}
        onClose={handleModalClose}
        editingItem={editingItem}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PaymentBillingCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get unique row key for installment table
 */
function getInstallmentRowKey(record, index) {
  return `${record.number || index}-${record.dueDate || ''}-${record.amount || 0}`;
}
